import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Ship, Anchor, ArrowLeft, Plus, ChevronDown, ChevronUp, Pencil, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ShipDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [shipData, setShipData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showOlderReports, setShowOlderReports] = useState(false);
  const [showAddReport, setShowAddReport] = useState(false);
  const [showEditReport, setShowEditReport] = useState(false);
  const [showUpdateFacts, setShowUpdateFacts] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const token = localStorage.getItem('token');

  const [newReport, setNewReport] = useState({
    workload: 'Normal',
    seniors_attitude: 'Neutral',
    crew_nationality: '',
    food_quality: '😐',
    notes: ''
  });

  const [newFacts, setNewFacts] = useState({
    trade_route: '',
    salary_status: '',
    internet_availability: '',
    internet_type: '',
    internet_limit_value: '',
    internet_limit_unit: '',
    internet_limit_period: '',
    internet_limit_unlimited: '',
    internet_topup_available: '',
    internet_topup_data: '',
    internet_topup_price: ''
  });

  useEffect(() => {
    fetchShipData();
  }, [id]);

  const fetchShipData = async () => {
    try {
      const res = await axios.get(`${API}/ships/${id}`);
      setShipData(res.data);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load ship data');
      setLoading(false);
    }
  };

  const handleSubmitReport = async () => {
    try {
      await axios.post(`${API}/ships/${id}/reports`, newReport, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success('Report submitted successfully');
      setShowAddReport(false);
      fetchShipData();
      setNewReport({
        workload: 'Normal',
        seniors_attitude: 'Neutral',
        crew_nationality: '',
        food_quality: '😐',
        notes: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit report');
    }
  };

  const handleEditReport = async () => {
    try {
      const updates = {};
      if (newReport.workload !== editingReport.workload) updates.workload = newReport.workload;
      if (newReport.seniors_attitude !== editingReport.seniors_attitude) updates.seniors_attitude = newReport.seniors_attitude;
      if (newReport.crew_nationality !== editingReport.crew_nationality) updates.crew_nationality = newReport.crew_nationality;
      if (newReport.food_quality !== editingReport.food_quality) updates.food_quality = newReport.food_quality;
      if (newReport.notes !== editingReport.notes) updates.notes = newReport.notes;

      await axios.patch(`${API}/reports/${editingReport.id}`, updates, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success('Report updated successfully');
      setShowEditReport(false);
      setEditingReport(null);
      fetchShipData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update report');
    }
  };

  const openEditDialog = (report) => {
    setEditingReport(report);
    setNewReport({
      workload: report.workload,
      seniors_attitude: report.seniors_attitude,
      crew_nationality: report.crew_nationality || '',
      food_quality: report.food_quality,
      notes: report.notes || ''
    });
    setShowEditReport(true);
  };

  const handleUpdateFacts = async () => {
    const facts = [];

    const internetLimit =
      newFacts.internet_limit_unlimited === "Unlimited"
        ? "Unlimited"
        : `${newFacts.internet_limit_value}${newFacts.internet_limit_unit}/${newFacts.internet_limit_period}`;
    Object.entries(newFacts).forEach(([fact_type, value]) => {
      if (fact_type === "internet_limit_value" && internetLimit) {
        facts.push({ fact_type: "internet_limit", value: internetLimit });
      }
      else if (
        fact_type !== "internet_limit_unit" &&
        fact_type !== "internet_limit_period" &&
        fact_type !== "internet_limit_value" &&
        value
      ) {
        facts.push({ fact_type, value });
      }
    });

    if (facts.length === 0) {
      toast.error('Please select at least one fact to update');
      return;
    }

    try {
      await axios.post(`${API}/ships/${id}/stable-facts`, { facts }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success('Facts updated successfully');
      setShowUpdateFacts(false);
      fetchShipData();
      setNewFacts({
        trade_route: '',
        salary_status: '',
        internet_availability: '',
        internet_type: '',
        internet_limit_value: '',
        internet_limit_unit: '',
        internet_limit_period: '',
        internet_limit_unlimited: '',
        internet_topup_available: '',
        internet_topup_data: '',
        internet_topup_price: ''
      });
    } catch (error) {
      toast.error('Failed to update facts');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="loading-spinner">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!shipData) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="error-message">
        <div className="text-muted-foreground">Ship not found</div>
      </div>
    );
  }

  const { ship, stable_facts, recent_reports, older_reports, reports_stats, total_recent_reports, last_reported, has_recent_data } = shipData;

  return (
    <div className="min-h-screen pb-20">
      <header className="sticky top-0 z-40 w-full backdrop-blur-lg bg-background/80 border-b border-border/40 h-16 flex items-center px-4 justify-between">
        <Button
          variant="ghost"
          size="sm"
          className="rounded-full"
          onClick={() => navigate('/')}
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back
        </Button>
        <div className="flex items-center gap-2">
          <Anchor className="w-5 h-5 text-primary" />
          <span className="font-heading font-bold" data-testid="header-app-name">MarineKnow</span>
        </div>
      </header>

      <div className="max-w-md mx-auto w-full p-4">
        <div className="bg-card border border-white/5 rounded-xl p-6 mb-6" data-testid="ship-header">
          <div className="flex items-start justify-between mb-2">
            <div>
              <h1 className="text-2xl font-heading font-bold mb-1" data-testid="ship-name">{ship.name}</h1>
              <div className="flex gap-2 flex-wrap">
                <Badge variant="secondary" data-testid="ship-type">{ship.ship_type}</Badge>
                {ship.imo_number && (
                  <Badge variant="outline" data-testid="imo-number">{ship.imo_number}</Badge>
                )}
              </div>
            </div>
            <Ship className="w-8 h-8 text-primary" />
          </div>
          <div className="mt-4 space-y-1 text-sm">
            <p className="text-muted-foreground" data-testid="total-reports">
              Total Reports (last 6 months): <span className="text-foreground font-semibold">{total_recent_reports}</span>
            </p>
            {last_reported && (
              <p className="text-muted-foreground" data-testid="last-reported">
                Last Reported: <span className="text-foreground">{format(new Date(last_reported), 'MMM d, yyyy')}</span>
              </p>
            )}
            <p className="text-xs text-muted-foreground/70 mt-2" data-testid="data-note">
              Data reflects last 6 months
            </p>
          </div>
        </div>

        {!has_recent_data && (
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 mb-6 flex items-start gap-3" data-testid="no-recent-data-warning">
            <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-yellow-500">No recent reports available</p>
              <p className="text-xs text-muted-foreground mt-1">Be the first to update information for this ship</p>
            </div>
          </div>
        )}

        <div className="bg-card border border-white/5 rounded-xl p-5 mb-6" data-testid="stable-facts-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-heading font-semibold" data-testid="stable-facts-title">Stable Facts</h2>
            <Button
              size="sm"
              variant="ghost"
              className="rounded-full"
              onClick={() => setShowUpdateFacts(true)}
              data-testid="update-facts-button"
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          <div className="space-y-4">
            {Object.entries(stable_facts).map(([key, data]) => (
              <div key={key} className="border-b border-border/30 pb-3 last:border-0" data-testid={`fact-${key}`}>
                <div className="flex justify-between items-start mb-2">
                  <span className="text-sm text-muted-foreground capitalize" data-testid={`fact-label-${key}`}>
                    {key.replace(/_/g, ' ')}
                  </span>
                </div>
                {data.values && data.values.length > 0 ? (
                  <div className="space-y-1">
                    {data.values.map(([value, percentage], idx) => (
                      <div key={idx} className="flex justify-between items-center text-sm">
                        <span className="font-medium" data-testid={`fact-value-${key}-${idx}`}>{value}</span>
                        <span className="text-primary font-semibold" data-testid={`fact-percentage-${key}-${idx}`}>{percentage}%</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Not reported</p>
                )}
                <p className="text-xs text-muted-foreground mt-2" data-testid={`fact-count-${key}`}>
                  Based on {data.count} {data.count === 1 ? 'report' : 'reports'}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-card border border-white/5 rounded-xl p-5 mb-6" data-testid="current-reality-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-heading font-semibold" data-testid="current-reality-title">Current Reality</h2>
            <Button
              size="sm"
              className="rounded-full"
              onClick={() => setShowAddReport(true)}
              data-testid="add-report-button"
            >
              <Plus className="w-4 h-4 mr-1" />
              Report
            </Button>
          </div>

          {reports_stats && total_recent_reports > 0 && (
            <div className="mb-4 p-3 bg-muted/30 rounded-lg space-y-2" data-testid="reports-stats">
              <p className="text-xs font-semibold text-muted-foreground uppercase">Summary ({total_recent_reports} reports)</p>
              {reports_stats.workload && Object.keys(reports_stats.workload).length > 0 && (
                <div className="text-sm">
                  <span className="text-muted-foreground">Workload: </span>
                  {Object.entries(reports_stats.workload).map(([k, v], i, arr) => (
                    <span key={k}>
                      {k} ({v}%){i < arr.length - 1 ? ', ' : ''}
                    </span>
                  ))}
                </div>
              )}
              {reports_stats.seniors_attitude && Object.keys(reports_stats.seniors_attitude).length > 0 && (
                <div className="text-sm">
                  <span className="text-muted-foreground">Seniors: </span>
                  {Object.entries(reports_stats.seniors_attitude).map(([k, v], i, arr) => (
                    <span key={k}>
                      {k} ({v}%){i < arr.length - 1 ? ', ' : ''}
                    </span>
                  ))}
                </div>
              )}
              {reports_stats.food_quality && Object.keys(reports_stats.food_quality).length > 0 && (
                <div className="text-sm">
                  <span className="text-muted-foreground">Food: </span>
                  {Object.entries(reports_stats.food_quality).map(([k, v], i, arr) => (
                    <span key={k}>
                      {k} ({v}%){i < arr.length - 1 ? ', ' : ''}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {recent_reports.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6" data-testid="no-reports-message">
              No reports yet. Be the first to contribute!
            </p>
          ) : (
            <div className="space-y-3" data-testid="recent-reports">
              {recent_reports.map((report) => (
                <div
                  key={report.id}
                  className="bg-card/50 border-l-4 border-l-primary/50 border-y border-r border-white/5 p-4 rounded-r-lg"
                  data-testid={`report-${report.id}`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <span className="text-xs text-muted-foreground" data-testid={`report-date-${report.id}`}>
                        {format(new Date(report.created_at), 'MMM d, yyyy')}
                      </span>
                      {report.updated_at && (
                        <span className="text-xs text-muted-foreground ml-2">(edited)</span>
                      )}
                      <div className="flex items-center gap-2 mt-1">
                        <Badge 
                          variant="outline" 
                          className="text-xs"
                          data-testid={`report-badge-${report.id}`}
                        >
                          {report.is_verified ? (
                            <span className="flex items-center gap-1">
                              <Anchor className="w-3 h-3" />
                              Verified Sailor
                            </span>
                          ) : (
                            'Guest'
                          )}
                        </Badge>
                      </div>
                    </div>
                    {report.can_edit && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0"
                        onClick={() => openEditDialog(report)}
                        data-testid={`edit-report-${report.id}`}
                      >
                        <Pencil className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm mb-2">
                    <div>
                      <span className="text-muted-foreground">Workload:</span>{' '}
                      <span data-testid={`report-workload-${report.id}`}>{report.workload}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Food:</span>{' '}
                      <span data-testid={`report-food-${report.id}`}>{report.food_quality}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Seniors:</span>{' '}
                      <span data-testid={`report-seniors-${report.id}`}>{report.seniors_attitude}</span>
                    </div>
                  </div>
                  {report.crew_nationality && (
                    <p className="text-xs text-muted-foreground mb-1" data-testid={`report-crew-${report.id}`}>
                      Crew: {report.crew_nationality}
                    </p>
                  )}
                  {report.notes && (
                    <p className="text-sm mt-2" data-testid={`report-notes-${report.id}`}>{report.notes}</p>
                  )}
                </div>
              ))}
            </div>
          )}

          {older_reports.length > 0 && (
            <div className="mt-4">
              <Button
                variant="ghost"
                className="w-full rounded-full"
                onClick={() => setShowOlderReports(!showOlderReports)}
                data-testid="toggle-older-reports-button"
              >
                {showOlderReports ? (
                  <>
                    <ChevronUp className="w-4 h-4 mr-1" />
                    Hide older reports
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-4 h-4 mr-1" />
                    View older reports ({older_reports.length})
                  </>
                )}
              </Button>

              {showOlderReports && (
                <div className="space-y-3 mt-4" data-testid="older-reports">
                  {older_reports.map((report) => (
                    <div
                      key={report.id}
                      className="bg-muted/30 border border-white/5 p-4 rounded-lg opacity-70"
                      data-testid={`older-report-${report.id}`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <span className="text-xs text-muted-foreground" data-testid={`older-report-date-${report.id}`}>
                            {format(new Date(report.created_at), 'MMM d, yyyy')}
                          </span>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="text-xs">
                              {report.is_verified ? (
                                <span className="flex items-center gap-1">
                                  <Anchor className="w-3 h-3" />
                                  Verified Sailor
                                </span>
                              ) : (
                                'Guest'
                              )}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm mb-2">
                        <div>
                          <span className="text-muted-foreground">Workload:</span> {report.workload}
                        </div>
                        <div>
                          <span className="text-muted-foreground">Food:</span> {report.food_quality}
                        </div>
                        <div>
                          <span className="text-muted-foreground">Seniors:</span> {report.seniors_attitude}
                        </div>
                      </div>
                      {report.crew_nationality && (
                        <p className="text-xs text-muted-foreground mb-1">
                          Crew: {report.crew_nationality}
                        </p>
                      )}
                      {report.notes && (
                        <p className="text-sm mt-2">{report.notes}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <Dialog open={showAddReport} onOpenChange={setShowAddReport}>
        <DialogContent className="bg-card border-border max-h-[90vh] overflow-y-auto" data-testid="add-report-dialog">
          <DialogHeader>
            <DialogTitle data-testid="add-report-title">Submit Report</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label data-testid="workload-label">Workload</Label>
              <Select value={newReport.workload} onValueChange={(v) => setNewReport({ ...newReport, workload: v })}>
                <SelectTrigger className="mt-1" data-testid="workload-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="Light" data-testid="workload-light">Light</SelectItem>
                  <SelectItem value="Normal" data-testid="workload-normal">Normal</SelectItem>
                  <SelectItem value="Heavy" data-testid="workload-heavy">Heavy</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label data-testid="seniors-label">Seniors' Attitude</Label>
              <Select value={newReport.seniors_attitude} onValueChange={(v) => setNewReport({ ...newReport, seniors_attitude: v })}>
                <SelectTrigger className="mt-1" data-testid="seniors-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="Supportive" data-testid="seniors-supportive">Supportive</SelectItem>
                  <SelectItem value="Neutral" data-testid="seniors-neutral">Neutral</SelectItem>
                  <SelectItem value="Toxic" data-testid="seniors-toxic">Toxic</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label data-testid="food-label">Food Quality</Label>
              <Select value={newReport.food_quality} onValueChange={(v) => setNewReport({ ...newReport, food_quality: v })}>
                <SelectTrigger className="mt-1" data-testid="food-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="👍" data-testid="food-good">👍 Good</SelectItem>
                  <SelectItem value="😐" data-testid="food-ok">😐 OK</SelectItem>
                  <SelectItem value="👎" data-testid="food-bad">👎 Bad</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="crew" data-testid="crew-label">Crew Nationality (Optional)</Label>
              <Input
                id="crew"
                type="text"
                value={newReport.crew_nationality}
                onChange={(e) => setNewReport({ ...newReport, crew_nationality: e.target.value })}
                placeholder="e.g., Indian, Filipino, Mixed"
                className="mt-1"
                data-testid="crew-input"
              />
            </div>

            <div>
              <Label htmlFor="notes" data-testid="notes-label">Notes (Max 300 characters)</Label>
              <Textarea
                id="notes"
                value={newReport.notes}
                onChange={(e) => setNewReport({ ...newReport, notes: e.target.value.slice(0, 300) })}
                placeholder="Additional observations..."
                className="mt-1"
                rows={3}
                data-testid="notes-textarea"
              />
              <p className="text-xs text-muted-foreground mt-1" data-testid="notes-counter">
                {newReport.notes.length}/300
              </p>
            </div>

            <Button onClick={handleSubmitReport} className="w-full" data-testid="submit-report-button">
              Submit Report
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showEditReport} onOpenChange={setShowEditReport}>
        <DialogContent className="bg-card border-border max-h-[90vh] overflow-y-auto" data-testid="edit-report-dialog">
          <DialogHeader>
            <DialogTitle data-testid="edit-report-title">Edit Report</DialogTitle>
            <p className="text-xs text-muted-foreground mt-1">You can edit within 24 hours of submission</p>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Workload</Label>
              <Select value={newReport.workload} onValueChange={(v) => setNewReport({ ...newReport, workload: v })}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="Light">Light</SelectItem>
                  <SelectItem value="Normal">Normal</SelectItem>
                  <SelectItem value="Heavy">Heavy</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Seniors' Attitude</Label>
              <Select value={newReport.seniors_attitude} onValueChange={(v) => setNewReport({ ...newReport, seniors_attitude: v })}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="Supportive">Supportive</SelectItem>
                  <SelectItem value="Neutral">Neutral</SelectItem>
                  <SelectItem value="Toxic">Toxic</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Food Quality</Label>
              <Select value={newReport.food_quality} onValueChange={(v) => setNewReport({ ...newReport, food_quality: v })}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="👍">👍 Good</SelectItem>
                  <SelectItem value="😐">😐 OK</SelectItem>
                  <SelectItem value="👎">👎 Bad</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="edit-crew">Crew Nationality (Optional)</Label>
              <Input
                id="edit-crew"
                type="text"
                value={newReport.crew_nationality}
                onChange={(e) => setNewReport({ ...newReport, crew_nationality: e.target.value })}
                placeholder="e.g., Indian, Filipino, Mixed"
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="edit-notes">Notes (Max 300 characters)</Label>
              <Textarea
                id="edit-notes"
                value={newReport.notes}
                onChange={(e) => setNewReport({ ...newReport, notes: e.target.value.slice(0, 300) })}
                placeholder="Additional observations..."
                className="mt-1"
                rows={3}
              />
              <p className="text-xs text-muted-foreground mt-1">
                {newReport.notes.length}/300
              </p>
            </div>

            <Button onClick={handleEditReport} className="w-full">
              Update Report
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showUpdateFacts} onOpenChange={setShowUpdateFacts}>
        <DialogContent className="bg-card border-border max-h-[90vh] overflow-y-auto" data-testid="update-facts-dialog">
          <DialogHeader>
            <DialogTitle data-testid="update-facts-title">Update Stable Facts</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label data-testid="trade-route-label">Trade Route</Label>
              <Select value={newFacts.trade_route} onValueChange={(v) => setNewFacts({ ...newFacts, trade_route: v })}>
                <SelectTrigger className="mt-1" data-testid="trade-route-select">
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="Long">Long</SelectItem>
                  <SelectItem value="Short">Short</SelectItem>
                  <SelectItem value="Mixed">Mixed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Salary Status</Label>
              <Select value={newFacts.salary_status} onValueChange={(v) => setNewFacts({ ...newFacts, salary_status: v })}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="On time">On time</SelectItem>
                  <SelectItem value="Sometimes delayed">Sometimes delayed</SelectItem>
                  <SelectItem value="Often delayed">Often delayed</SelectItem>
                </SelectContent>
              </Select>
            </div>                  

            <div>
              <Label>Internet Available</Label>
              <Select
                value={newFacts.internet_availability}
                onValueChange={(v) =>
                  setNewFacts({
                    ...newFacts,
                    internet_availability: v,
                    internet_type: v === "No" ? "" : newFacts.internet_type,
                    internet_limit_value: v === "No" ? "" : newFacts.internet_limit_value,
                    internet_limit_unit: v === "No" ? "" : newFacts.internet_limit_unit,
                    internet_limit_period: v === "No" ? "" : newFacts.internet_limit_period,
                    internet_limit_unlimited: v === "No" ? "" : newFacts.internet_limit_unlimited,
                    internet_topup_available: v === "No" ? "" : newFacts.internet_topup_available,
                    internet_topup_data: v === "No" ? "" : newFacts.internet_topup_data,
                    internet_topup_price: v === "No" ? "" : newFacts.internet_topup_price
                  })
               }
             >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="Yes">Yes</SelectItem>
                  <SelectItem value="No">No</SelectItem>
                </SelectContent>
              </Select>
               
            </div>

            {newFacts.internet_availability === "Yes" && (
              <>
                <div>
                  <Label>Internet Type</Label>
                  <Select
                    value={newFacts.internet_type}
                    onValueChange={(v) =>
                      setNewFacts({ ...newFacts, internet_type: v })  
                    }
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      <SelectItem value="VSAT">VSAT</SelectItem>
                      <SelectItem value="Starlink">Starlink</SelectItem>
                      <SelectItem value="Inmarsat">Inmarsat</SelectItem>
                      <SelectItem value="Unknown">Unknown</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Internet Limit</Label>
                      
                  <Select
                    value={newFacts.internet_limit_unlimited}
                    onValueChange={(v) =>
                      setNewFacts({
                        ...newFacts,
                        internet_limit_unlimited: v
                      })
                    }
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      <SelectItem value="Limited">Limited</SelectItem>
                      <SelectItem value="Unlimited">Unlimited</SelectItem>
                    </SelectContent>
                  </Select>

                  {newFacts.internet_limit_unlimited === "Limited" && (
                    <div className="grid grid-cols-3 gap-2 mt-2">

                      <Select
                        value={newFacts.internet_limit_value}
                        onValueChange={(v) =>
                          setNewFacts({ ...newFacts, internet_limit_value: v })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Amount" />
                        </SelectTrigger>
                        <SelectContent className="bg-card border-border">
                          <SelectItem value="1">1</SelectItem>
                          <SelectItem value="2">2</SelectItem>
                          <SelectItem value="3">3</SelectItem>
                          <SelectItem value="5">5</SelectItem>
                          <SelectItem value="10">10</SelectItem>
                          <SelectItem value="20">20</SelectItem>
                          <SelectItem value="50">50</SelectItem>
                          <SelectItem value="100">100</SelectItem>
                        </SelectContent>
                      </Select>

                      <Select
                        value={newFacts.internet_limit_unit}
                        onValueChange={(v) =>
                          setNewFacts({ ...newFacts, internet_limit_unit: v })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Unit" />
                        </SelectTrigger>
                        <SelectContent className="bg-card border-border">
                          <SelectItem value="MB">MB</SelectItem>
                          <SelectItem value="GB">GB</SelectItem>
                        </SelectContent>
                     </Select>

                      <Select
                        value={newFacts.internet_limit_period}
                        onValueChange={(v) =>
                          setNewFacts({ ...newFacts, internet_limit_period: v })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Period" />
                        </SelectTrigger>
                        <SelectContent className="bg-card border-border">
                          <SelectItem value="day">Day</SelectItem>
                          <SelectItem value="week">Week</SelectItem>
                          <SelectItem value="month">Month</SelectItem>
                        </SelectContent>
                      </Select>

                    </div>
                  )}
                </div>                      

                {newFacts.internet_limit_unlimited !== "Unlimited" && (
                  <>
                    <div>
                      <Label>Internet Top-up Available</Label>
                      <Select
                        value={newFacts.internet_topup_available}
                        onValueChange={(v) =>
                          setNewFacts({
                            ...newFacts,
                            internet_topup_available: v
                          })
                        }
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Select..." />
                        </SelectTrigger>
                        <SelectContent className="bg-card border-border">
                          <SelectItem value="Yes">Yes</SelectItem>
                          <SelectItem value="No">No</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {newFacts.internet_topup_available === "Yes" && (
                      <>
                        <div>
                          <Label>Top-up Data Amount</Label>
                          <Select
                            value={newFacts.internet_topup_data}
                            onValueChange={(v) =>
                              setNewFacts({
                                ...newFacts,
                                internet_topup_data: v
                              })
                            }
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select..." />
                            </SelectTrigger>
                            <SelectContent className="bg-card border-border">
                              <SelectItem value="1GB">1 GB</SelectItem>
                              <SelectItem value="2GB">2 GB</SelectItem>
                              <SelectItem value="5GB">5 GB</SelectItem>
                              <SelectItem value="10GB">10 GB</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                          <Label>Top-up Price (USD)</Label>
                          <Input
                            type="number"
                            placeholder="e.g. 10"
                            value={newFacts.internet_topup_price}
                            onChange={(e) =>
                              setNewFacts({
                                ...newFacts,
                                internet_topup_price: e.target.value
                              })
                            }
                            className="mt-1"
                          />
                        </div>
                      </>
                    )}
                  </>
                )}

              </>
            )}

            <Button
              onClick={handleUpdateFacts}
              className="w-full"
              data-testid="submit-facts-button"
            >
              Submit Updates
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ShipDetail;
